import { cn } from "@gettactic/helpers";
import React from "react";
import type {
  ComparisonCandidate,
  FloorplanComparison
} from "../../../../utils/requestFloorplanComparison";

type Area = ComparisonCandidate["areas"][number];

export interface FastImportSourceOverlayProps {
  snapshot: FloorplanComparison;
  /** Natural size of the source image the overlay sits on. */
  width: number;
  height: number;
}

interface Tile {
  id: string;
  label: string;
  bounds: NonNullable<Area["bounds"]>;
  status: "working" | "done" | "failed";
  desks: number;
  rooms: number;
}

/** Group a tile's walls and furniture jobs into one on-screen tile. */
const toTiles = (areas: Area[]): Tile[] => {
  const byTile = new Map<string, Area[]>();
  for (const area of areas)
    if (area.tileId && area.bounds)
      byTile.set(area.tileId, [...(byTile.get(area.tileId) || []), area]);
  return [...byTile.entries()].map(([id, jobs]) => ({
    id,
    label: jobs[0].label.split(" · ")[0],
    bounds: jobs[0].bounds!,
    status: jobs.some((j) => j.status === "failed" || j.status === "cancelled")
      ? "failed"
      : jobs.every((j) => j.status === "completed")
        ? "done"
        : "working",
    desks: jobs.reduce((sum, j) => sum + (j.counts?.desks || 0), 0),
    rooms: jobs.reduce((sum, j) => sum + (j.counts?.rooms || 0), 0)
  }));
};

const tileCaption = (tile: Tile) => {
  const found = [
    tile.desks ? `${tile.desks} desk${tile.desks === 1 ? "" : "s"}` : "",
    tile.rooms ? `${tile.rooms} room${tile.rooms === 1 ? "" : "s"}` : ""
  ]
    .filter(Boolean)
    .join(", ");
  if (tile.status === "failed") return `${tile.label} · needs a retry`;
  if (tile.status === "done") return `${tile.label} · ${found || "done"}`;
  return `${tile.label} · ${found || "drawing…"}`;
};

export interface FastImportTilesProps {
  snapshot: FloorplanComparison;
  /**
   * Map units per image pixel. The finished map is scaled up so desk glyphs
   * fit (see calibrateImportedDesks), so inside the map preview this is
   * map width / image width; over the source image it is 1.
   */
  scale?: number;
}

/**
 * Live progress drawn in image pixel coordinates: the locally detected wall
 * sketch right away, each tile pulsing while it works, and what each tile has
 * found so far. Usable inside the map preview's SVG or over the source image.
 */
export const FastImportTiles: React.FC<FastImportTilesProps> = ({
  snapshot,
  scale = 1
}) => {
  const candidate = snapshot.candidates[0];
  if (!candidate || !snapshot.width || !snapshot.height) return null;
  const tiles = toTiles(candidate.areas);
  const isRunning = candidate.status === "in_progress";
  const baseFontSize = Math.max(snapshot.width, snapshot.height) / 60;
  return (
    <g
      className="pointer-events-none"
      transform={scale === 1 ? undefined : `scale(${scale})`}
    >
      {isRunning &&
        snapshot.sketch?.map(([x1, y1, x2, y2]) => (
          <line
            key={`${x1},${y1},${x2},${y2}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            className="stroke-blue-500/60"
            strokeWidth={2}
            strokeDasharray="6 5"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      {tiles.map((tile) => (
        <g key={tile.id}>
          <rect
            x={tile.bounds.x}
            y={tile.bounds.y}
            width={tile.bounds.width}
            height={tile.bounds.height}
            vectorEffect="non-scaling-stroke"
            strokeWidth={1.5}
            className={cn(
              tile.status === "working" &&
                "animate-pulse fill-blue-500/10 stroke-blue-400 motion-reduce:animate-none",
              tile.status === "done" && "fill-transparent stroke-green-500/60",
              tile.status === "failed" && "fill-red-500/10 stroke-red-500"
            )}
            strokeDasharray={tile.status === "working" ? "8 6" : undefined}
          />
          {(isRunning || tile.status === "failed") && (
            <TileCaption tile={tile} baseFontSize={baseFontSize} />
          )}
        </g>
      ))}
    </g>
  );
};

interface TileCaptionProps {
  tile: Tile;
  baseFontSize: number;
}

/** Centered in its tile and shrunk to fit, so narrow neighbours never overlap. */
const TileCaption: React.FC<TileCaptionProps> = ({ tile, baseFontSize }) => {
  const caption = tileCaption(tile);
  // Average glyph width is about 0.55em; leave a margin on both sides.
  const fontSize = Math.min(
    baseFontSize,
    (tile.bounds.width * 0.9) / (caption.length * 0.55),
    tile.bounds.height / 3
  );
  return (
    <text
      x={tile.bounds.x + tile.bounds.width / 2}
      y={tile.bounds.y + tile.bounds.height / 2}
      fontSize={fontSize}
      textAnchor="middle"
      dominantBaseline="middle"
      paintOrder="stroke"
      strokeWidth={fontSize / 5}
      className={cn(
        "fill-neutral-800 stroke-white font-medium",
        tile.status === "failed" && "fill-red-700"
      )}
    >
      {caption}
    </text>
  );
};

/** The same live progress, laid over the source `<img>` (object-contain). */
export const FastImportSourceOverlay: React.FC<
  FastImportSourceOverlayProps
> = ({ snapshot, width, height }) => {
  if (width <= 1 || height <= 1) return null;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden="true"
    >
      <FastImportTiles snapshot={snapshot} />
    </svg>
  );
};
